"use client";

/**
 * Shared client body for the /compare/* pages. One component, three pages —
 * the copy lives in the `comparePages.<page>` namespace (marketing/compare.ts)
 * and follows the visitor's locale via useT(), like every marketing page.
 *
 * These pages answer "isn't this just X?" head-on (V4.4): honest about what
 * the neighbouring category is good at, explicit about the architectural
 * difference, and explicit about when the other approach is enough.
 */

import Link from "next/link";
import { ArrowLeft, ArrowRight, ShieldCheck } from "lucide-react";
import { MarketingShell } from "@/components/marketing/marketing-shell";
import { useT } from "@/i18n";

export type ComparePageKey = "workflowAutomation" | "enterpriseSearch" | "servicenow";

const DIFF_KEYS = ["d1", "d2", "d3"] as const;

export function CompareContent({ page }: { page: ComparePageKey }) {
  const t = useT();
  const k = (s: string) => `comparePages.${page}.${s}`;
  return (
    <MarketingShell>
      <section className="relative overflow-hidden border-b border-line-subtle">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 -top-32 h-[420px] bg-[radial-gradient(ellipse_at_top,rgba(244,63,142,0.14),transparent_60%)]"
        />
        <div className="relative mx-auto max-w-3xl px-5 pt-16 pb-12 sm:px-6 md:pt-24 md:pb-16">
          <div className="text-[11px] font-medium uppercase tracking-[0.16em] text-brand-300">
            {t("comparePages.shared.eyebrow")}
          </div>
          <h1 className="mt-4 text-balance text-4xl font-semibold leading-[1.08] tracking-tight text-fg-bright md:text-5xl">
            {t(k("title"))}
          </h1>
          <p className="mt-6 text-[15px] leading-relaxed text-fg-dim md:text-base">{t(k("intro1"))}</p>
          <p className="mt-4 text-[15px] leading-relaxed text-fg-dim md:text-base">{t(k("intro2"))}</p>
        </div>
      </section>

      <section className="border-b border-line-subtle">
        <div className="mx-auto max-w-5xl px-5 py-16 sm:px-6 md:py-20">
          <h2 className="text-2xl font-semibold tracking-tight text-fg-bright md:text-3xl">
            {t(k("diffsTitle"))}
          </h2>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {DIFF_KEYS.map((d) => (
              <div key={d} className="card h-full p-6">
                <ShieldCheck size={16} className="text-brand-300" />
                <div className="mt-3 text-[15px] font-semibold text-fg-bright">
                  {t(k(`diffs.${d}.title`))}
                </div>
                <p className="mt-2 text-[13.5px] leading-relaxed text-fg-dim">
                  {t(k(`diffs.${d}.body`))}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-line-subtle">
        <div className="mx-auto max-w-3xl px-5 py-16 sm:px-6 md:py-20">
          <h2 className="text-xl font-semibold tracking-tight text-fg-bright md:text-2xl">
            {t(k("whenTitle"))}
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-fg-dim">{t(k("whenBody"))}</p>
        </div>
      </section>

      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(244,63,142,0.12),transparent_65%)]"
        />
        <div className="relative mx-auto max-w-3xl px-5 py-20 text-center sm:px-6 md:py-24">
          <h2 className="text-balance text-3xl font-semibold tracking-tight text-fg-bright md:text-4xl">
            {t("comparePages.shared.ctaTitle")}
          </h2>
          <p className="mt-4 text-[15px] text-fg-dim">{t("comparePages.shared.ctaBody")}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              href="/signup"
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-grad px-5 py-3 text-[14px] font-semibold text-white shadow-glow hover:brightness-110 transition"
            >
              {t("comparePages.shared.ctaPrimary")}
              <ArrowRight size={15} />
            </Link>
            <Link
              href="/demo"
              className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-bg-raised px-4 py-2.5 text-[14px] text-fg hover:bg-bg-hover hover:border-line-strong transition"
            >
              {t("comparePages.shared.ctaSecondary")}
            </Link>
          </div>
          <Link
            href="/"
            className="mt-8 inline-flex items-center gap-1 text-[13px] text-fg-muted hover:text-fg transition"
          >
            <ArrowLeft size={13} />
            {t("comparePages.shared.backLabel")}
          </Link>
        </div>
      </section>
    </MarketingShell>
  );
}
